import { DestroyRef, Injectable, Renderer2, RendererFactory2, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { timer } from 'rxjs';

/** Upper bound on a sweep loop, so a selector that never stops matching cannot hang the teardown. */
const MAX_SWEEP_ITERATIONS = 50;

/**
 * Modal artefacts that have no business being a direct child of `<body>`.
 *
 * Everything Angular renders lives under `<app-root>`, so anything here that
 * turns up as a body child is an orphan: either Bootstrap relocated it, or it
 * outlived the component that declared it. Matching is done against the element
 * itself, never a descendant.
 */
const ORPHANED_MODAL_SELECTOR =
    '.modal, .modal-backdrop, .popup-backdrop, ngb-modal-window, ngb-modal-backdrop';

@Injectable({
    providedIn: 'root'
})
export class ModalService {
    public modalStack: { id: string; ref: any }[] = [];
    public isAnyDropdownOpen = false;
    private isHandlingEscape = false;
    private readonly renderer: Renderer2;
    private readonly destroyRef = inject(DestroyRef);

    /** Active only while a session teardown is in progress. See `setTeardownGuard`. */
    private reattachmentObserver: MutationObserver | null = null;

    constructor() {
        const rendererFactory = inject(RendererFactory2);

        this.renderer = rendererFactory.createRenderer(null, null);
    }

    /**
   * Watches `<body>` for dialogs being re-attached, and detaches them again.
   *
   * Sweeping once is not enough. Bootstrap 5.2 shows a dialog in two stages —
   * the backdrop fades, then a queued callback runs `_showElement`, which does
   * `document.body.contains(el) || document.body.append(el)`. When a session
   * ends mid-flight that callback lands *after* Angular has destroyed the
   * component and removed the element, so `contains` is false and Bootstrap
   * re-attaches the dead dialog to `<body>`. It then belongs to no view, so no
   * later navigation can remove it: it floats over every screen that follows,
   * which is exactly the dialog that survived a redirect to the welcome screen
   * and was still there two routes later.
   *
   * A `MutationObserver` is the only cleanup that outlasts that callback
   * without guessing at a delay. It is scoped to the teardown — switched on
   * when a session ends, off by the next sign-in — so nothing observes `<body>`
   * during normal use.
   *
   * Direct DOM access is deliberate here: this service is the wrapper that owns
   * the Bootstrap modal lifecycle, which is where the standards put it.
   */
    setTeardownGuard(isActive: boolean): void {
        if (!isActive) {
            this.reattachmentObserver?.disconnect();
            this.reattachmentObserver = null;
            return;
        }
        if (this.reattachmentObserver) {
            return; // already guarding this teardown
        }

        const body = this.renderer.selectRootElement('body', true) as HTMLElement;
        this.reattachmentObserver = new MutationObserver(records => {
            records.forEach(record =>
                record.addedNodes.forEach(node => this.detachIfOrphanedModal(node))
            );
        });
        this.reattachmentObserver.observe(body, { childList: true });
    }

    /** Removes a node the observer saw arrive, if it is a modal artefact. */
    private detachIfOrphanedModal(node: Node): void {
        const element = node as Partial<HTMLElement>;
        if (typeof element.matches !== 'function' || !element.matches(ORPHANED_MODAL_SELECTOR)) {
            return;
        }
        this.renderer.removeChild(this.renderer.parentNode(node), node);
    }

    register(id: string, ref: any): void {
        const exists = this.modalStack.find(modal => modal.id === id);
        if (!exists) {
            this.modalStack.push({ id, ref });
        }
    }

    unregister(id: string): void {
        this.modalStack = this.modalStack.filter(m => m.id !== id);
    }

    getTopModal(): any {
        return this.modalStack[this.modalStack.length - 1]?.ref;
    }

    getTopModalId(): string | null {
        return this.modalStack[this.modalStack.length - 1]?.id ?? null;
    }

    getZIndex(id: string): number {
        const index = this.modalStack.findIndex(m => m.id === id);
        return 1055 + index * 2;
    }

    clearAll(): void {
        this.modalStack = [];
    }

    /**
   * Close every registered modal (triggers each modal's closeModal handler),
   * clear the stack, then sweep for any leftover Bootstrap backdrop / modal
   * artefacts that the close animation did not remove in time.
   *
   * Custom modals append a `.modal-backdrop` element directly to `<body>` via
   * Bootstrap's data-bs-* system. When Angular navigates away before the
   * animation completes those nodes are orphaned and must be removed.
   *
   * All DOM access goes exclusively through Angular's Renderer2 —
   * no DOCUMENT token, no raw JS property assignments.
   */
    closeAll(): void {
        const stackSnapshot = [...this.modalStack];
        this.modalStack = [];
        for (const modal of stackSnapshot) {
            if (modal.ref && typeof modal.ref.closeModal === 'function') {
                try { modal.ref.closeModal(); } catch { /* ignore errors during teardown */ }
            }
        }

        // Bootstrap relocates a dialog to <body> when it opens it. That lifts the
        // element out of the view Angular tears down on navigation, so the modal
        // outlives the component that declared it and no route change will ever
        // remove it — it simply floats above every screen that follows. Everything
        // Angular renders lives under <app-root>, so a modal artefact sitting as a
        // direct child of <body> is an orphan by construction and safe to detach.
        this.removeOrphanedBodyChildren();

        // Then hide anything still marked as open but *inside* a live view: those
        // nodes Angular still owns, and detaching them would corrupt the view.
        this.hideAllBySelector('.modal.show');

        // Restore the body to its default (non-modal) state.
        const body = this.renderer.selectRootElement('body', true);
        this.renderer.removeClass(body, 'modal-open');
        this.renderer.removeStyle(body, 'overflow');
        this.renderer.removeStyle(body, 'padding-right');
    }

    /**
   * Detaches every modal artefact that has been reparented to `<body>`.
   *
   * `.modal` is the Bootstrap dialog itself — the one that survives navigation
   * and is the reason a dialog could stay on screen through two route changes.
   * The backdrops are its siblings, and the `ngb-*` hosts cover an ng-bootstrap
   * modal that `dismissAll` failed to take down.
   */
    private removeOrphanedBodyChildren(): void {
        ORPHANED_MODAL_SELECTOR
            .split(',')
            .forEach(selector => this.removeAllBySelector(`body > ${selector.trim()}`));
    }

    /**
   * Removes every element matching `selector` from the DOM via Renderer2.
   * `selectRootElement` acts like `querySelector` and throws when nothing
   * matches — that throw is the exit condition for the loop.
   */
    private removeAllBySelector(selector: string): void {
        let safety = MAX_SWEEP_ITERATIONS;
        while (safety-- > 0) {
            try {
                const el = this.renderer.selectRootElement(selector, true);
                this.renderer.removeChild(this.renderer.parentNode(el), el);
            } catch {
                break; // no more matching elements
            }
        }
    }

    /**
   * Hides every element matching `selector` (removes `.show`, sets
   * `display: none`) via Renderer2. Same loop-until-throw pattern.
   *
   * The aria attributes are Bootstrap's, set when it opened the dialog.
   * Reverting them is what makes this a close rather than a hide — otherwise
   * the dialog stays in the accessibility tree, announced to screen readers as
   * the active modal, while being invisible to everyone else.
   */
    private hideAllBySelector(selector: string): void {
        let safety = MAX_SWEEP_ITERATIONS;
        while (safety-- > 0) {
            try {
                const el = this.renderer.selectRootElement(selector, true);
                this.renderer.removeClass(el, 'show');
                this.renderer.setStyle(el, 'display', 'none');
                this.renderer.removeAttribute(el, 'aria-modal');
                this.renderer.removeAttribute(el, 'role');
                this.renderer.setAttribute(el, 'aria-hidden', 'true');
            } catch {
                break; // no more matching elements
            }
        }
    }

    closeTopModalOnEscape(): void {
        if (this.isAnyDropdownOpen) { return; }
        if (this.isHandlingEscape) { return; } // Prevent double close
        this.isHandlingEscape = true;

        const top = this.getTopModal();
        if (top && typeof top.closeModal === 'function') {
            top.closeModal(); //call the close Modal function from the common component so that this event gets triggered
        }

        // Reset flag after short delay to allow next Escape (an RxJS timer, not setTimeout: review MR !1 S17)
        timer(100).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.isHandlingEscape = false;
        });

    }

    openDropdown(): void {
        this.isAnyDropdownOpen = true;
    }
    closeDropdown(): void {
        this.isAnyDropdownOpen = false;
    }
}
